import React from 'react';
import { Link } from 'react-router-dom';

const RecallPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Recall - Smart Flashcards by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: August 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Recall - Smart Flashcards ("Recall", "the App") is developed and operated by Wolf
              Technologies Ltd. We are committed to protecting your privacy and handling your data
              responsibly. This policy explains what data we collect, how we use it, and your rights
              regarding your personal information.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>When you use Recall, we collect the following data:</p>
            <ul>
              <li><strong>Email address</strong> — used for account creation and authentication.</li>
              <li><strong>Flashcard content</strong> — the decks and cards you create, including front/back text and any associated metadata.</li>
              <li><strong>Study progress and statistics</strong> — review history, spaced repetition scheduling data, streak information, and performance metrics.</li>
              <li><strong>Subscription status</strong> — your current plan (free or premium) and billing status.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We want to be clear about what we do not collect:</p>
            <ul>
              <li><strong>Location data</strong></li>
              <li><strong>Photos or media</strong> from your device</li>
              <li><strong>Contacts</strong> or address book data</li>
              <li><strong>Browsing history</strong></li>
              <li><strong>Biometric data</strong></li>
              <li><strong>Advertising identifiers</strong></li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>Your data is used solely to provide and improve the Recall flashcard service:</p>
            <ul>
              <li><strong>Provide the flashcard service</strong> — storing your decks, cards, and review history so you can study effectively.</li>
              <li><strong>Sync across devices</strong> — keeping your flashcard data consistent across all your devices when you are signed in.</li>
              <li><strong>Spaced repetition scheduling</strong> — using the FSRS algorithm to calculate optimal review intervals based on your study history and performance.</li>
            </ul>
            <p>
              We do not use your data for advertising, profiling, or any purpose unrelated to the core
              flashcard service.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <ul>
              <li>All user data is stored on <strong>Amazon Web Services (AWS)</strong> in the <strong>eu-west-2 (London)</strong> region.</li>
              <li>Data is <strong>encrypted at rest</strong> using AWS-managed encryption keys.</li>
              <li>Data is <strong>encrypted in transit</strong> using TLS/HTTPS for all communications between the app and our servers.</li>
              <li>User authentication is managed through <strong>AWS Cognito</strong>, which provides secure, standards-based authentication.</li>
              <li>We follow security best practices for access control and infrastructure management.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>We use the following third-party services to operate Recall:</p>
            <ul>
              <li><strong>AWS Cognito</strong> — user authentication and account management.</li>
              <li><strong>AWS DynamoDB</strong> — secure storage of your flashcard data, study progress, and account information.</li>
              <li><strong>Apple In-App Purchase</strong> — payment processing for subscriptions, managed entirely by Apple.</li>
              <li><strong>AWS Bedrock</strong> — processes text you choose to paste into AI deck generation, solely to produce your flashcards. Pasted text is not used to train models.</li>
            </ul>
            <p>
              These services have their own privacy policies and are bound by their respective data
              processing agreements.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <ul>
              <li><strong>We do not sell your data</strong> to any third party.</li>
              <li><strong>We do not share your data</strong> with third parties for marketing or advertising purposes.</li>
              <li>Data is shared only with the services listed above, as strictly necessary to process your subscription payments and provide the features you use.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li>Your data is retained for as long as your account is active.</li>
              <li>You can delete your account and all associated data at any time through the app settings.</li>
              <li>Upon account deletion, all your flashcard content, study history, statistics, and personal information are permanently removed from our servers.</li>
              <li>Deletion is irreversible — we cannot recover data after it has been deleted.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the General Data Protection Regulation (GDPR) and applicable UK data protection law,
              you have the following rights:
            </p>
            <ul>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — request correction of inaccurate personal data.</li>
              <li><strong>Erasure</strong> — request deletion of your personal data.</li>
              <li><strong>Data portability</strong> — request your data in a structured, machine-readable format.</li>
              <li><strong>Objection</strong> — object to the processing of your personal data.</li>
              <li><strong>Restriction</strong> — request that we limit how we use your data.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. If we make significant changes, we will
              notify you through the app or by email. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>
              If you have any questions about this privacy policy or how we handle your data, please
              contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default RecallPrivacy;
